/**
 * ReviewSheet (Форма отзыва) — leave a 1–5★ rating + comment for a completed order.
 *
 * The product, order and date are shown read-only (they come from the order — ТЗ §12); the
 * user only picks a star rating and writes an optional comment. On success the created
 * review is handed back via ``onSubmitted`` so the caller can refresh ``can_review`` state
 * (the order's review button then disappears).
 *
 * Posting is server-validated: only a COMPLETED, not-yet-reviewed order accepts a review.
 */
import { Sheet } from "@melusine/shared-ui";
import {
  MAX_RATING,
  MIN_RATING,
  postOrderReview,
  type Review,
} from "@entities/review";
import { ApiError } from "@shared/api";
import { useState } from "react";

import styles from "./ReviewSheet.module.css";

export interface ReviewSheetProps {
  readonly open: boolean;
  readonly orderId: number;
  /** Human order number (e.g. ``MS-000123``) shown read-only. */
  readonly orderNumber: string;
  /** Product title shown read-only; falls back to a neutral label when unknown. */
  readonly productTitle?: string;
  /** Order date (ISO) shown read-only. */
  readonly createdAt?: string;
  readonly onClose: () => void;
  readonly onSubmitted: (review: Review) => void;
}

function errorText(err: ApiError): string {
  const payload = err.payload as { readonly detail?: unknown } | null;
  if (payload !== null && typeof payload.detail === "string") {
    return payload.detail;
  }
  return "Не удалось отправить отзыв. Попробуйте ещё раз.";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function ReviewSheet({
  open,
  orderId,
  orderNumber,
  productTitle,
  createdAt,
  onClose,
  onSubmitted,
}: ReviewSheetProps): JSX.Element {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    if (rating < MIN_RATING || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    postOrderReview(orderId, {
      rating,
      ...(text.trim().length > 0 ? { text: text.trim() } : {}),
    })
      .then((review) => {
        onSubmitted(review);
      })
      .catch((err: unknown) => {
        setError(
          errorText(
            err instanceof ApiError ? err : new ApiError(0, "Network error", err),
          ),
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const shown = hover || rating;

  return (
    <Sheet
      open={open}
      title="Оставить отзыв"
      dismissible={!isSubmitting}
      onClose={onClose}
    >
      <div className={styles.body}>
        <dl className={styles.meta}>
          <div className={styles.metaRow}>
            <dt className={styles.metaLabel}>Товар</dt>
            <dd className={styles.metaValue}>{productTitle ?? "Заказ"}</dd>
          </div>
          <div className={styles.metaRow}>
            <dt className={styles.metaLabel}>Заказ</dt>
            <dd className={styles.metaValue}>{orderNumber}</dd>
          </div>
          {createdAt ? (
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>Дата</dt>
              <dd className={styles.metaValue}>{formatDate(createdAt)}</dd>
            </div>
          ) : null}
        </dl>

        <div
          className={styles.stars}
          role="radiogroup"
          aria-label="Оценка от 1 до 5"
        >
          {Array.from({ length: MAX_RATING }, (_, i) => i + 1).map((value) => (
            <button
              key={value}
              type="button"
              className={styles.star}
              data-filled={value <= shown}
              role="radio"
              aria-checked={value === rating}
              aria-label={`${value} из ${MAX_RATING}`}
              onMouseEnter={() => setHover(value)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(value)}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" aria-hidden="true">
                <path d="m12 3 2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.7 6.8 19.2l1-5.8L3.5 9.2l5.9-.9z" />
              </svg>
            </button>
          ))}
        </div>

        <textarea
          className={styles.comment}
          placeholder="Поделитесь впечатлениями о покупке…"
          value={text}
          maxLength={4000}
          rows={4}
          onChange={(event) => setText(event.target.value)}
        />

        {error !== null ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className={styles.submit}
          disabled={rating < MIN_RATING || isSubmitting}
          onClick={submit}
        >
          {isSubmitting ? "Отправляем…" : "Отправить отзыв"}
        </button>
      </div>
    </Sheet>
  );
}
